import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { usersKey } from '../../../solaris-lens-common-lib/src/constants/queryKeys/Users';
import {
  searchUsers,
  getUsers,
  getUserProfile,
  updateUserV3,
  createUserV3,
  deleteUser
} from '../../../solaris-lens-common-lib/src/services/users';
import { RootState } from '../../../store';
import { SearchUsersResponse } from '../../../types/users';
import useShouldRefresh from '@/hooks/useShouldRefresh';
import { USER_PAGE_SIZE } from '@/solaris-lens-common-lib/src/constants';

export interface UserUpdateType {
  id?: string;
  username: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  companyId?: string;
  enabled: boolean;
  groups: string;
  password?: string;
}

export const useSearchUsers = (
  param: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<SearchUsersResponse>({
    queryKey: usersKey.search(token, param, 'qemail'),
    queryFn: () => {
      return searchUsers(
        token,
        param,
        'qemail'
      ) as Promise<SearchUsersResponse>;
    },
    enabled: Boolean(token && param),
    ...useQueryOptions
  });
};

/**
 * Hook to fetch initial users list for modal selection
 * @param {Object} useQueryOptions - React Query options
 * @returns {UseQueryResult<UsersResponse>} Query result with users data
 */
export const useGetUsersInitial = ({ useQueryOptions = {} } = {}) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<UsersResponse>({
    queryKey: [...usersKey.lists(token), 'initial', USER_PAGE_SIZE],
    queryFn: () => {
      return getUsers(
        token,
        undefined,
        undefined,
        USER_PAGE_SIZE
      ) as Promise<UsersResponse>;
    },
    enabled: Boolean(token),
    ...useQueryOptions
  });
};

interface UseGetUsersListParams {
  pageSize?: number;
  paginationToken?: string;
  groupName?: string;
  searchTerm?: string;
  searchType?: string;
}

interface UsersResponse {
  info: {
    paginator: {
      paginationToken: string;
    };
  };
  data: any[];
}

export const useGetUsersList = (
  params: UseGetUsersListParams = {},
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  const {
    pageSize = USER_PAGE_SIZE,
    paginationToken,
    groupName,
    searchTerm,
    searchType = 'qemail'
  } = params;

  return useQuery<UsersResponse>({
    queryKey: [
      ...usersKey.lists(token),
      pageSize,
      paginationToken,
      groupName,
      searchTerm,
      searchType
    ],
    queryFn: () => {
      if (searchTerm) {
        return searchUsers(
          token,
          searchTerm,
          searchType
        ) as Promise<UsersResponse>;
      }
      return getUsers(
        token,
        paginationToken,
        groupName,
        pageSize
      ) as Promise<UsersResponse>;
    },
    enabled: Boolean(token),
    ...useQueryOptions
  });
};

export const useGetUserDetails = (
  username: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: usersKey.detail(token, username),
    queryFn: async () => {
      const response = await getUserProfile(token, username);
      const userProfile = {
        ...response.data,
        ...response.data.attributes,
        roles: response.data.attributes.groups[0]
      };
      return userProfile;
    },
    enabled: Boolean(token && username),
    ...useQueryOptions
  });
};

const createUserParams = (userData: UserUpdateType) => {
  return {
    username: userData.username,
    email: userData.email,
    password: userData.password,
    isEmailVerified: 'false',
    customAtributes: {
      'custom:company': userData.company,
      'custom:companyId': parseInt(userData.companyId || ''),
      'custom:name': userData.name,
      'custom:phone': userData.phone,
      // 'custom:groups': userData.groups,
      'custom:isActive': userData.enabled ? 1 : 0
    }
  };
};

const updateUserParams = (userData: UserUpdateType) => {
  return {
    username: userData.username,
    name: userData.name,
    phone: userData.phone,
    company: userData.company,
    companyId: parseInt(userData.companyId || ''),
    enabled: userData.enabled.toString(),
    group: userData.groups.toString()
  };
};

export const useCreateUser = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: UserUpdateType) => {
      return createUserV3(token, createUserParams(userData));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKey.all });
    },
    onError: error => {
      console.error('Error saving user:', error);
    }
  });
};

export const useUpdateUser = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: UserUpdateType) => {
      return updateUserV3(token, updateUserParams(userData));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKey.all });
    },
    onError: error => {
      console.error('Error saving user:', error);
    }
  });
};

export const useDeleteUser = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (username: string) => {
      return deleteUser(token, username);
    },
    onSuccess: (_, username) => {
      queryClient.removeQueries({
        queryKey: usersKey.detail(token, username)
      });
      queryClient.invalidateQueries({ queryKey: usersKey.lists(token) });
    },
    onError: error => {
      console.error('Error deleting user:', error);
    }
  });
};
